// Sprint 14.0 — Motor de comandos ESC/POS + disparo híbrido (Bluetooth RawBT / Servicio local / Nativo)
// 100% cliente. Persiste preferencias de hardware en localStorage por dispositivo.
import { fmtDateTime, getLocalWeekday } from "./datetime";
import { buildCorteModel } from "./corteModel";

const ESC = 0x1b;
const GS = 0x1d;

export const PRINTER_CONFIG_KEY = "parknova_printer_config";

export const defaultPrinterConfig = {
  modo: "nativo", // "bluetooth" | "local" | "usb" | "nativo"
  papel: "80", // "58" | "80"
  abrirCajon: false,
  autocorte: true,
  host: "127.0.0.1",
  port: 8080, // ParkNova Print Agent (soporta spooler + puertos COM/Bluetooth)
  printerName: "", // nombre del spooler o puerto COM (ej. "COM6") elegido en el agente
  usbVendorId: null,
  usbProductId: null,
  usbName: "",
};

export const loadPrinterConfig = () => {
  try {
    return { ...defaultPrinterConfig, ...JSON.parse(localStorage.getItem(PRINTER_CONFIG_KEY) || "{}") };
  } catch {
    return { ...defaultPrinterConfig };
  }
};

export const savePrinterConfig = (cfg) => {
  localStorage.setItem(PRINTER_CONFIG_KEY, JSON.stringify(cfg));
};

// ---------- helpers de bytes ----------
const enc = (str) => {
  const out = [];
  for (let i = 0; i < str.length; i++) out.push(str.charCodeAt(i) & 0xff);
  return out;
};

// Sprint 41.2 — Codificación Latina para ESC/POS (acentos, ñ, ¿, ¡). Mapea Unicode → bytes de la
// code page CP850 (que se selecciona con ESC t 2). Las vocales acentuadas minúsculas y ñ/Ñ comparten
// posición entre CP850 y CP437, por lo que imprimen bien aun en impresoras que defaultean a CP437.
const CP850 = {
  "á": 0xa0, "é": 0x82, "í": 0xa1, "ó": 0xa2, "ú": 0xa3, "ñ": 0xa4, "Ñ": 0xa5,
  "ü": 0x81, "Ü": 0x9a, "Á": 0xb5, "É": 0x90, "Í": 0xd6, "Ó": 0xe0, "Ú": 0xe9,
  "¿": 0xa8, "¡": 0xad, "°": 0xf8, "ª": 0xa6, "º": 0xa7, "«": 0xae, "»": 0xaf,
};
const TRANSLIT = { "Â": "A", "Ê": "E", "Î": "I", "Ô": "O", "Û": "U", "à": "a", "è": "e", "ç": "c", "Ç": "C", "€": "EUR" };
const SELECT_CP850 = [ESC, 0x74, 0x02]; // ESC t 2 → CP850 (Multilingual Latin-1)
const encL = (str) => {
  const out = [];
  const s = String(str == null ? "" : str);
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const code = s.charCodeAt(i);
    if (code < 0x80) { out.push(code); continue; }
    if (CP850[ch] != null) { out.push(CP850[ch]); continue; }
    const t = TRANSLIT[ch];
    if (t != null) { for (let j = 0; j < t.length; j++) out.push(t.charCodeAt(j)); continue; }
    out.push(0x3f); // '?' en vez de un símbolo basura
  }
  return out;
};

const toBase64 = (bytes) => {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};

// Ajuste de texto simple por ancho de columnas (para términos largos)
const wrapText = (text, width = 32) => {
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > width) { if (cur) lines.push(cur); cur = w; }
    else cur = (cur ? cur + " " : "") + w;
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [""];
};

// Sprint 37.0 — Maquetación ESC/POS por ancho de papel:
//   58mm -> 32 caracteres/línea (Font A) · 80mm -> 48 caracteres/línea.
// Provee separador exacto, recorte y filas de 2 columnas (concepto izq / monto der) que suman `cols`.
const ticketLayout = (cfg = {}) => {  const cols = String(cfg.papel) === "58" ? 32 : 48;
  const half = Math.max(1, Math.floor(cols / 2));   // límite para texto en DBL (doble ancho)
  const dash = "-".repeat(cols);
  const clip = (s) => String(s).slice(0, cols);
  const clipHalf = (s) => String(s).slice(0, half); // recorta texto que se imprime en doble ancho
  const row = (label, val) => {
    let l = String(label);
    const r = String(val);
    const maxL = Math.max(1, cols - r.length - 1); // deja al menos 1 espacio entre columnas
    if (l.length > maxL) l = l.slice(0, maxL);
    const space = Math.max(1, cols - l.length - r.length);
    return l + " ".repeat(space) + r; // longitud exacta = cols
  };
  return { cols, half, dash, clip, clipHalf, row };
};

// Ancho del logo (dots) según el papel: 58mm = 384px, 80mm = 576px (cabeza de impresión completa).
export const logoWidthForPaper = (cfg = {}) => (String(cfg.papel) === "58" ? 384 : 576);

// Vista previa en TEXTO plano de un ticket representativo con el ancho del papel elegido
// (reutiliza ticketLayout para que coincida 1:1 con lo que imprime la térmica).
export const buildTicketPreview = (cfg = {}) => {
  const { cols, dash, row, clip } = ticketLayout(cfg);
  const center = (s) => {
    s = clip(s);
    const pad = Math.max(0, Math.floor((cols - s.length) / 2));
    return " ".repeat(pad) + s;
  };
  const lines = [
    center("PARKNOVA"),
    center("- COMPROBANTE DE PAGO -"),
    dash,
    row("Folio:", "EST-000123"),
    row("Placas:", "ABC-1234"),
    row("Fecha:", "01/06/26 10:15"),
    row("Metodo pago:", "Efectivo"),
    dash,
    row("Estancia", "$60.00"),
    row("Servicio Valet", "$50.00"),
    row("Subtotal", "$110.00"),
    row("Propina", "$15.00"),
    dash,
    center("TOTAL: $125.00"),
    dash,
    center("Gracias por su preferencia"),
  ];
  return { cols, lines };
};

// ---------- comandos ESC/POS estándar ----------
const INIT = [ESC, 0x40]; // ESC @  (inicialización)
const CUT = [GS, 0x56, 0x01]; // GS V 1 (autocorte parcial)
const DRAWER = [ESC, 0x70, 0x00, 0x19, 0xfa]; // ESC p 0 25 250 (apertura de cajón)
const ALIGN_CENTER = [ESC, 0x61, 0x01];
const ALIGN_LEFT = [ESC, 0x61, 0x00];
const BOLD_ON = [ESC, 0x45, 0x01];
const BOLD_OFF = [ESC, 0x45, 0x00];
const DBL = [GS, 0x21, 0x11]; // doble ancho + alto
const NORMAL = [GS, 0x21, 0x00];
const FONT_B = [ESC, 0x4d, 0x01]; // ESC M 1 -> Fuente B (comprimida)
const FONT_A = [ESC, 0x4d, 0x00]; // ESC M 0 -> Fuente A (normal)
const LF = [0x0a];
// Sprint 41.3 — Avance de ~5mm (~40 dots @203dpi) para margen libre superior/inferior del Corte.
const FEED_5MM = [ESC, 0x4a, 0x28];

// QR nativo del comando ESC/POS (GS ( k) — modelo 2
const qrCommands = (data) => {
  const bytes = enc(data);
  const store = [];
  store.push(GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00); // modelo 2
  store.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, 0x06); // tamaño de módulo 6
  store.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31); // corrección de error M
  const len = bytes.length + 3;
  store.push(GS, 0x28, 0x6b, len & 0xff, (len >> 8) & 0xff, 0x31, 0x50, 0x30, ...bytes); // almacenar
  store.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30); // imprimir
  return store;
};

// ---------- constructor de buffer ----------
// Ticket de prueba requerido: "PARKNOVA - PRUEBA EXITOSA" + logo ASCII + QR.
export const buildTestTicket = (cfg = {}) => {
  const b = [];
  b.push(...INIT);
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...DBL);
  b.push(...enc("PARKNOVA"), ...LF);
  b.push(...NORMAL, ...BOLD_OFF);
  b.push(...enc("=========================="), ...LF);
  b.push(...enc("        [ P ]"), ...LF);
  b.push(...enc("      P A R K N O V A"), ...LF);
  b.push(...enc("=========================="), ...LF, ...LF);
  b.push(...BOLD_ON, ...enc("PARKNOVA - PRUEBA EXITOSA"), ...BOLD_OFF, ...LF, ...LF);
  b.push(...qrCommands("PARKNOVA-TEST"), ...LF);
  b.push(...enc("Folio de prueba: TEST-0001"), ...LF);
  b.push(...enc(fmtDateTime(new Date())), ...LF);
  b.push(...enc(`Papel: ${cfg.papel || "80"}mm`), ...LF, ...LF, ...LF);
  b.push(...ALIGN_LEFT);
  if (cfg.abrirCajon) b.push(...DRAWER);
  if (cfg.autocorte) b.push(...CUT);
  return new Uint8Array(b);
};

// Constructor genérico de un ticket de venta/valet (folio + líneas + total)
export const buildTicket = ({ brand = "PARKNOVA", lines = [], folio, total, qrData, cfg = {} }) => {
  const b = [];
  const { cols, half, dash, clip, clipHalf } = ticketLayout(cfg);
  b.push(...INIT, ...ALIGN_CENTER, ...BOLD_ON, ...DBL);
  wrapText(String(brand), half).forEach((ln) => b.push(...enc(ln), ...LF));
  b.push(...NORMAL, ...BOLD_OFF);
  b.push(...enc(dash), ...LF, ...ALIGN_LEFT, ...BOLD_ON);
  lines.forEach((l) => b.push(...enc(clip(String(l))), ...LF));
  b.push(...BOLD_OFF);
  if (total != null) {
    b.push(...enc(dash), ...LF, ...BOLD_ON, ...DBL);
    b.push(...ALIGN_CENTER, ...enc(clipHalf(`TOTAL: $${Number(total).toFixed(2)}`)), ...LF, ...NORMAL, ...BOLD_OFF, ...ALIGN_LEFT);
  }
  if (qrData) {
    b.push(...LF, ...ALIGN_CENTER, ...qrCommands(String(qrData)), ...LF);
    b.push(...enc(String(folio || qrData)), ...LF, ...ALIGN_LEFT);
  }
  b.push(...LF, ...LF);
  if (cfg.abrirCajon) b.push(...DRAWER);
  b.push(...FEED_5MM); // margen libre inferior ~5mm (tras el último contenido, antes del corte físico)
  if (cfg.autocorte) b.push(...CUT);
  return new Uint8Array(b);
};

// ---------- plataforma ----------
export const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
  const android = /Android/.test(ua);
  return { iOS, android };
};

// ---------- disparadores ----------
// Android: RawBT vía esquema intent (envía el buffer base64 a la app externa)
export const printViaRawBT = (bytes) => {
  const b64 = toBase64(bytes);
  const url = `intent:base64,${b64}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
  window.location.href = url;
};

// PC: ParkNova Print Agent (127.0.0.1:8080). Se usa el loopback 127.0.0.1 porque Chrome lo trata
// como origen "potencialmente seguro" y NO lo bloquea por Mixed Content al llamarlo desde HTTPS
// (no requiere habilitar "Contenido no seguro" en el navegador).
const AGENT_TIMEOUT_MS = 5000;
const agentUrl = (host, port, path) => `http://${host || "127.0.0.1"}:${port || 8080}${path}`;

// fetch con timeout + errores limpios: evita cuelgues y devuelve un mensaje claro si el agente
// no está corriendo o la conexión fue rechazada/bloqueada.
const agentFetch = async (url, options = {}, timeout = AGENT_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    return await fetch(url, { ...options, signal: controller.signal, cache: "no-store" });
  } catch (e) {
    if (e?.name === "AbortError") {
      throw new Error("El agente no respondió a tiempo. Verifica que esté en ejecución.");
    }
    // TypeError típico: agente apagado, puerto cerrado o conexión rechazada.
    throw new Error("No se pudo conectar con el agente en http://127.0.0.1:8080. Descárgalo y ábrelo primero.");
  } finally {
    clearTimeout(timer);
  }
};

// Healthcheck silencioso del agente local (para indicadores de estado / pre-chequeo).
export const pingLocalAgent = async ({ host = "127.0.0.1", port = 8080 } = {}) => {
  try {
    const res = await agentFetch(agentUrl(host, port, "/"), { method: "GET" }, 3000);
    return res.ok;
  } catch {
    return false;
  }
};

// Envía los bytes ESC/POS en Base64 + printer_name para que el agente los reenvíe al spooler
// de Windows o a un puerto COM/Bluetooth.
export const printViaLocalService = async (bytes, { host = "127.0.0.1", port = 8080, printerName = "" } = {}) => {
  const res = await agentFetch(agentUrl(host, port, "/print"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ printer_name: printerName || null, data: toBase64(bytes) }),
  }, 15000);
  let payload = null;
  try { payload = await res.json(); } catch { /* respuesta no-JSON */ }
  if (!res.ok || (payload && payload.ok === false)) {
    throw new Error((payload && payload.error) || `El agente de impresión respondió ${res.status}`);
  }
  return payload || true;
};

// Lista las impresoras detectadas por el agente local (spooler + puertos COM/Bluetooth).
export const fetchLocalPrinters = async ({ host = "127.0.0.1", port = 8080 } = {}) => {
  const res = await agentFetch(agentUrl(host, port, "/printers"), { method: "GET" }, 6000);
  if (!res.ok) throw new Error(`El agente de impresión respondió ${res.status}`);
  const data = await res.json();
  return Array.isArray(data?.printers) ? data.printers : [];
};

// ---------- WebUSB (USB Directo cableado, sin servidor local) ----------
export const usbSupported = () => typeof navigator !== "undefined" && !!navigator.usb;

// Selector nativo del navegador. El permiso se otorga una sola vez por dispositivo/origen.
export const requestUsbDevice = async () => {
  if (!usbSupported()) throw new Error("WebUSB no está soportado. Usa Chrome o Edge en PC.");
  const device = await navigator.usb.requestDevice({ filters: [] });
  return device;
};

// Recupera un dispositivo ya autorizado (persistencia del permiso) por vendorId/productId.
export const getGrantedUsbDevice = async (cfg) => {
  if (!usbSupported()) return null;
  const devices = await navigator.usb.getDevices();
  if (cfg && cfg.usbVendorId != null) {
    const m = devices.find((d) => d.vendorId === cfg.usbVendorId && d.productId === cfg.usbProductId);
    if (m) return m;
  }
  return devices[0] || null;
};

// Detección automática de endpoint: recorre interfaces y devuelve el primer endpoint 'out'.
const findOutEndpoint = (device) => {
  const conf = device.configuration;
  if (!conf) return null;
  for (const iface of conf.interfaces) {
    for (const alt of iface.alternates) {
      for (const ep of alt.endpoints) {
        if (ep.direction === "out") {
          return { interfaceNumber: iface.interfaceNumber, endpointNumber: ep.endpointNumber };
        }
      }
    }
  }
  return null;
};

// Envío directo del buffer ESC/POS por WebUSB.
export const printViaWebUSB = async (bytes, cfg) => {
  const device = await getGrantedUsbDevice(cfg);
  if (!device) throw new Error("No hay impresora USB vinculada.");
  await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  const target = findOutEndpoint(device);
  if (!target) throw new Error("No se encontró un endpoint de salida en la impresora USB.");
  await device.claimInterface(target.interfaceNumber);
  await device.transferOut(target.endpointNumber, bytes);
  try { await device.releaseInterface(target.interfaceNumber); } catch { /* noop */ }
  try { await device.close(); } catch { /* noop */ }
  return true;
};

// iOS / Nativo: abre una ventana con CSS @page al ancho correcto y dispara window.print()
export const printNativePopup = ({ papel = "80", qrDataUrl, title = "PARKNOVA", body = "" } = {}) => {
  const width = papel === "58" ? 58 : 80;
  const win = window.open("", "PARKNOVA_PRINT", "width=360,height=640");
  if (!win) throw new Error("El navegador bloqueó la ventana. Permite ventanas emergentes e intenta de nuevo.");
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
  <style>
    @page { size: ${width}mm auto; margin: 0; }
    * { margin:0; padding:0; box-sizing:border-box; }
    body { width:${width}mm; font-family:'Courier New',monospace; color:#000; text-align:center; padding:3mm; }
    .brand{ font-size:18px; font-weight:800; letter-spacing:1px; }
    .logo{ white-space:pre; font-size:10px; line-height:1.15; margin:2mm 0; }
    .ok{ font-size:14px; font-weight:800; margin:2mm 0; }
    img{ width:60%; margin:2mm auto; display:block; image-rendering:pixelated; }
    .sm{ font-size:10px; line-height:1.4; }
    .sep{ border-top:1px dashed #000; margin:2mm 0; }
  </style></head><body>${body}
  <script>window.onload=function(){setTimeout(function(){window.print();},350);};<\/script>
  </body></html>`);
  win.document.close();
  win.focus();
  return win;
};

export const buildTestNativeBody = (papel, qrDataUrl) => `
  <div class="brand">PARKNOVA</div>
  <div class="logo">[ P ]\nP A R K N O V A</div>
  <div class="sep"></div>
  <div class="ok">PARKNOVA - PRUEBA EXITOSA</div>
  ${qrDataUrl ? `<img src="${qrDataUrl}" alt="QR"/>` : ""}
  <div class="sm">Folio de prueba: TEST-0001</div>
  <div class="sm">${fmtDateTime(new Date())}</div>
  <div class="sep"></div>
  <div class="sm">Modo: Nativo / AirPrint &middot; ${papel}mm</div>
`;

// Disparo maestro para buffers ESC/POS (bluetooth/local/usb). Para nativo se usa printNativePopup.
export const printBuffer = async (bytes, cfg) => {
  const c = cfg || loadPrinterConfig();
  if (c.modo === "bluetooth") {
    printViaRawBT(bytes);
    return { modo: "bluetooth" };
  }
  if (c.modo === "local") {
    await printViaLocalService(bytes, { host: c.host, port: c.port, printerName: c.printerName });
    return { modo: "local" };
  }
  if (c.modo === "usb") {
    await printViaWebUSB(bytes, c);
    return { modo: "usb" };
  }
  throw new Error("El modo nativo no usa buffer ESC/POS; usa printNativePopup.");
};

// Pulso de apertura de cajón (ESC p 0 25 250)
export const buildDrawerPulse = () => new Uint8Array([ESC, 0x70, 0x00, 0x19, 0xfa]);

// Sprint 18.0 Etapa B — Ticket térmico de CORTE DE CAJA con matriz de liquidación y firmas.
export const buildCorteTicket = ({ brand = "PARKNOVA", sucursal, operador, resumen = {}, efectivoContado, reimpresion = false, cfg = {} } = {}) => {
  const b = [...INIT, ...SELECT_CP850, ...FEED_5MM]; // margen libre superior ~5mm (antes del primer texto)
  const { cols, dash, clip, row } = ticketLayout(cfg);
  const m = buildCorteModel(resumen, { efectivoContado, reimpresion });
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;
  const div = () => b.push(...encL(dash), ...LF);
  const center = (s) => { const t = clip(String(s)); const pad = Math.max(0, Math.floor((cols - t.length) / 2)); return " ".repeat(pad) + t; };
  const sucNombre = sucursal || resumen.nombre_sucursal || resumen.sucursal_nombre;

  // ---- Encabezado (una sola vez) ----
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...DBL, ...encL(clip(brand || "PARKNOVA")), ...NORMAL, ...LF);
  b.push(...encL("CORTE DE CAJA"), ...BOLD_OFF, ...LF, ...ALIGN_LEFT);
  if (sucNombre) b.push(...encL(row("Sucursal:", clip(sucNombre))), ...LF);
  if (operador || resumen.operador) b.push(...encL(row("Operador:", clip(operador || resumen.operador))), ...LF);
  // FECHA DE OPERACIÓN — grande, centrada, en negritas (= fecha de apertura del turno)
  b.push(...LF, ...ALIGN_CENTER, ...BOLD_ON, ...encL("FECHA DE OPERACION"), ...LF);
  b.push(...DBL, ...encL(m.operacionFecha), ...NORMAL, ...BOLD_OFF, ...LF, ...ALIGN_LEFT);
  b.push(...encL(row("Inicio de turno:", m.inicioTxt)), ...LF);
  b.push(...encL(row("Cierre de turno:", m.cierreTxt)), ...LF);
  if (m.reimpresion) b.push(...ALIGN_CENTER, ...BOLD_ON, ...encL("** REIMPRESION **"), ...BOLD_OFF, ...ALIGN_LEFT, ...LF);
  div();

  // ---- Liquidación (tickets) ----
  b.push(...BOLD_ON, ...encL("LIQUIDACION (TICKETS)"), ...BOLD_OFF, ...LF);
  b.push(...encL(row("Tickets generados", String(m.liquidacion.generados))), ...LF);
  b.push(...encL(row("(-) Cancelados", String(m.liquidacion.cancelados))), ...LF);
  b.push(...encL(row("(-) Cortesias", String(m.liquidacion.cortesias))), ...LF);
  b.push(...BOLD_ON, ...encL(row("Tickets efect. cobrados", String(m.liquidacion.cobrados))), ...BOLD_OFF, ...LF);
  div();

  // ---- Cálculo de importes (efectivo). Factor de riesgo se suma UNA sola vez. ----
  const im = m.importes;
  b.push(...BOLD_ON, ...encL("CALCULO DE IMPORTES"), ...BOLD_OFF, ...LF);
  b.push(...encL(row("Recaudacion (efectivo)", money(im.efvo))), ...LF);
  b.push(...encL(row("(+) Fondo inicial", money(im.fondo))), ...LF);
  if (im.fr > 0) b.push(...encL(row(`(+) Factor riesgo (${im.frN} x ${money(im.frCuota)})`, money(im.fr))), ...LF);
  b.push(...BOLD_ON, ...encL(row("(=) Subtotal", money(im.subtotal))), ...BOLD_OFF, ...LF);
  b.push(...encL(row("(-) Gastos del turno", money(im.gastos))), ...LF);
  b.push(...encL(row("(-) Retiro de propinas", money(im.propinas))), ...LF);
  div();

  // ---- Total efectivo a entregar (grande) ----
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...encL("TOTAL EFECTIVO A ENTREGAR"), ...LF);
  b.push(...DBL, ...encL(money(m.totalEntregar)), ...NORMAL, ...BOLD_OFF, ...ALIGN_LEFT, ...LF);
  div();

  // ---- Conciliación de caja (sin duplicar el factor de riesgo) ----
  const co = m.conciliacion;
  b.push(...BOLD_ON, ...encL("CONCILIACION DE CAJA"), ...BOLD_OFF, ...LF);
  b.push(...encL(row("Efectivo esperado", money(co.totalEntregar))), ...LF);
  if (co.contado != null) {
    b.push(...encL(row("Efectivo contado", money(co.contado))), ...LF);
    b.push(...BOLD_ON, ...encL(row("DIFERENCIA", money(co.diferencia))), ...LF);
    b.push(...ALIGN_CENTER, ...encL(co.estadoCaja), ...BOLD_OFF, ...ALIGN_LEFT, ...LF);
  }
  div();

  // ---- Desglose por concepto (solo habilitados; Productos solo si > 0) ----
  if (m.conceptos.length) {
    b.push(...BOLD_ON, ...encL("DESGLOSE POR CONCEPTO"), ...BOLD_OFF, ...LF);
    for (const c of m.conceptos) b.push(...encL(row(c.label, money(c.monto))), ...LF);
    if (m.pernoctas.n > 0) b.push(...encL(row(`(+) Pernoctas (${m.pernoctas.n})`, money(m.pernoctas.monto))), ...LF);
    div();
  }

  // ---- Vehículos en patio ----
  b.push(...BOLD_ON, ...encL("VEHICULOS EN PATIO"), ...BOLD_OFF, ...LF);
  b.push(...encL(row("Vehiculos en patio", String(m.patio.n))), ...LF);
  for (const f of m.patio.folios) b.push(...encL(clip(`  ${f.folio}${f.placa ? " / " + f.placa : ""}`)), ...LF);

  // ---- Gastos del turno ----
  if (m.gastosList.length) {
    div();
    b.push(...BOLD_ON, ...encL("GASTOS DEL TURNO"), ...BOLD_OFF, ...LF);
    for (const g of m.gastosList) {
      b.push(...encL(row(String(g.categoria || "Otro"), money(g.monto))), ...LF);
      if (g.concepto) wrapText(g.concepto, cols - 2).forEach((ln) => b.push(...encL(`  ${ln}`), ...LF));
    }
    b.push(...BOLD_ON, ...encL(row("Total gastos", money(im.gastos))), ...BOLD_OFF, ...LF);
  }

  // ---- Desglose de cancelados / cortesías ----
  if (m.cancelaciones.rows.length) {
    div();
    b.push(...BOLD_ON, ...encL("DESGLOSE DE CANCELADOS / CORTESIAS"), ...BOLD_OFF, ...LF);
    for (const c of m.cancelaciones.rows) {
      b.push(...encL(clip(`${String(c.folio).padEnd(7)}${String(c.tipo).padEnd(10)}`)), ...LF);
      if (c.motivo) wrapText(c.motivo, cols - 2).forEach((ln) => b.push(...encL(`  ${ln}`), ...LF));
    }
    b.push(...encL(row("Total cancelados", String(m.cancelaciones.totalCanc))), ...LF);
    b.push(...encL(row("Total cortesias", String(m.cancelaciones.totalCort))), ...LF);
  }

  b.push(...LF, ...ALIGN_CENTER, ...BOLD_ON, ...encL("GRACIAS"), ...LF, ...encL(clip(brand || "ParkNova")), ...BOLD_OFF, ...LF);
  b.push(...FEED_5MM); // margen libre inferior ~5mm (tras el último texto, antes del corte físico)
  if (cfg.autocorte !== false) b.push(...CUT);
  return new Uint8Array(b);
};

// Sprint 42.0 — Comprobante térmico de GASTO / NÓMINA (salida de efectivo) con dos firmas.
// Datos de SOLO LECTURA (no registra ni descuenta). Margen 5mm arriba/abajo + CP850 (encL).
export const buildGastoTicket = ({ brand = "PARKNOVA", sucursal, operador, fecha, categoria, concepto, monto, reimpresion = false, cfg = {} } = {}) => {
  const b = [...INIT, ...SELECT_CP850, ...FEED_5MM]; // margen libre superior ~5mm
  const { cols, dash, clip, row } = ticketLayout(cfg);
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;
  const div = () => b.push(...encL(dash), ...LF);
  const sigLine = "_".repeat(cols);

  // Encabezado (marca una vez + título + sucursal una vez)
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...DBL, ...encL(clip(brand || "PARKNOVA")), ...NORMAL, ...LF);
  b.push(...encL("COMPROBANTE DE GASTO"), ...BOLD_OFF, ...LF, ...ALIGN_LEFT);
  if (sucursal) b.push(...encL(row("Sucursal:", clip(sucursal))), ...LF);
  b.push(...encL(row("Fecha/Hora:", fecha || fmtDateTime(new Date()))), ...LF);
  if (operador) b.push(...encL(row("Responsable:", clip(operador))), ...LF);
  b.push(...encL(row("Categoria:", clip(categoria || "Otro"))), ...LF);
  if (reimpresion) b.push(...ALIGN_CENTER, ...BOLD_ON, ...encL("** REIMPRESION **"), ...BOLD_OFF, ...ALIGN_LEFT, ...LF);
  div();

  // Concepto completo (ajuste de línea, SIN recortes)
  b.push(...BOLD_ON, ...encL("CONCEPTO"), ...BOLD_OFF, ...LF);
  wrapText(String(concepto || "-"), cols).forEach((ln) => b.push(...encL(ln), ...LF));
  div();

  // Importe entregado (grande + negritas)
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...encL("IMPORTE ENTREGADO"), ...LF, ...DBL, ...encL(money(monto)), ...NORMAL, ...BOLD_OFF, ...ALIGN_LEFT, ...LF);
  div();

  // Firmas — ENTREGA (responsable) y RECIBE (quien recibe el efectivo), amplias.
  b.push(...LF, ...BOLD_ON, ...encL("ENTREGA"), ...BOLD_OFF, ...LF);
  if (operador) b.push(...encL(clip(operador)), ...LF);
  b.push(...LF, ...encL(sigLine), ...LF, ...encL("Nombre y firma"), ...LF);
  b.push(...LF, ...BOLD_ON, ...encL("RECIBE"), ...BOLD_OFF, ...LF, ...LF);
  b.push(...encL(sigLine), ...LF, ...encL("Nombre"), ...LF, ...LF);
  b.push(...encL(sigLine), ...LF, ...encL("Firma"), ...LF);

  // Margen libre inferior ~5mm antes del corte físico
  b.push(...LF, ...FEED_5MM);
  if (cfg.autocorte !== false) b.push(...CUT);
  return new Uint8Array(b);
};

// Sprint 43.0 — Comprobante térmico de CORTESÍA (valor documental; NO cobra ni mueve caja).
// Datos congelados al aplicar (monto_cortesia_otorgado). Margen 5mm + CP850 + dos firmas.
export const buildCortesiaTicket = ({ brand = "PARKNOVA", sucursal, operador, fecha, folio, motivo, importe, reimpresion = false, cfg = {} } = {}) => {
  const b = [...INIT, ...SELECT_CP850, ...FEED_5MM]; // margen libre superior ~5mm
  const { cols, dash, clip } = ticketLayout(cfg);
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;
  const div = () => b.push(...encL(dash), ...LF);
  const sigLine = "_".repeat(cols);

  // Encabezado (empresa/sucursal sin duplicar + título)
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...DBL, ...encL(clip(brand || "PARKNOVA")), ...NORMAL, ...LF);
  b.push(...encL("COMPROBANTE DE CORTESIA"), ...BOLD_OFF, ...LF, ...ALIGN_LEFT);
  if (sucursal) wrapText(`Sucursal: ${sucursal}`, cols).forEach((ln) => b.push(...encL(ln), ...LF));
  if (folio) b.push(...encL(clip(`Folio: ${folio}`)), ...LF);
  b.push(...encL(clip(`Fecha: ${fecha || fmtDateTime(new Date())}`)), ...LF);
  wrapText(`Motivo: ${motivo || "-"}`, cols).forEach((ln) => b.push(...encL(ln), ...LF));
  if (operador) wrapText(`Aplico: ${operador}`, cols).forEach((ln) => b.push(...encL(ln), ...LF));
  if (reimpresion) b.push(...ALIGN_CENTER, ...BOLD_ON, ...encL("** REIMPRESION **"), ...BOLD_OFF, ...ALIGN_LEFT, ...LF);
  div();

  // Importe de cortesía (grande + negritas) + leyenda
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...encL("IMPORTE DE CORTESIA"), ...LF, ...DBL, ...encL(money(importe)), ...NORMAL, ...BOLD_OFF, ...LF);
  b.push(...encL("Valor del servicio otorgado sin cobro"), ...ALIGN_LEFT, ...LF);
  div();

  // Firmas (ambas siempre; gerencia opcional)
  b.push(...ALIGN_CENTER);
  b.push(...LF, ...LF, ...encL(sigLine), ...LF, ...BOLD_ON, ...encL("Firma del encargado de turno"), ...BOLD_OFF, ...LF);
  b.push(...LF, ...LF, ...encL(sigLine), ...LF, ...BOLD_ON, ...encL("Firma de gerencia (si aplica)"), ...BOLD_OFF, ...LF);
  b.push(...ALIGN_LEFT);

  // Margen libre inferior ~5mm antes del corte físico
  b.push(...LF, ...FEED_5MM);
  if (cfg.autocorte !== false) b.push(...CUT);
  return new Uint8Array(b);
};

// Sprint 25.0 — Comprobante de Pago (recibo de dinero) del módulo Valet.
// Replica la robustez financiera de la Terminal: desglose de subtotal, propina,
// cargo por servicio (comisión) y total pagado.
export const buildValetPaymentReceipt = ({
  brand = "PARKNOVA", folio, placa, fecha, metodoPago,
  estancia = 0, valet = 0, penalizacion = 0, propina = 0, comision = 0, total = 0,
  cobradoPor, cfg = {},
} = {}) => {
  const b = [...INIT];
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;
  const { cols, dash, clip, row } = ticketLayout(cfg);
  const div = () => b.push(...enc(dash), ...LF);
  const est = Number(estancia || 0), val = Number(valet || 0), pen = Number(penalizacion || 0);
  const prop = Number(propina || 0), com = Number(comision || 0);
  const subtotal = Math.round((est + val + pen) * 100) / 100;

  // Encabezado (Font A + negrita para máxima nitidez)
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...DBL, ...enc("COMPROBANTE"), ...LF, ...enc("DE PAGO"), ...NORMAL, ...LF);
  b.push(...enc("- VALET -"), ...BOLD_OFF, ...LF);
  if (brand) { b.push(...BOLD_ON); wrapText(String(brand), cols).forEach((ln) => b.push(...enc(ln), ...LF)); b.push(...BOLD_OFF); }
  div();

  // Datos del ticket
  b.push(...ALIGN_LEFT);
  if (folio) b.push(...enc(row("Folio:", folio)), ...LF);
  if (placa) b.push(...enc(row("Placas:", placa)), ...LF);
  b.push(...enc(row("Fecha/Hora:", fecha || fmtDateTime(new Date()))), ...LF);
  if (metodoPago) b.push(...enc(row("Metodo pago:", metodoPago)), ...LF);
  if (cobradoPor) b.push(...enc(row("Atendio:", cobradoPor)), ...LF);
  div();

  // Desglose financiero (negrita para legibilidad en 58mm)
  b.push(...BOLD_ON, ...enc("DESGLOSE"), ...LF);
  if (est > 0) b.push(...enc(row("Estancia", money(est))), ...LF);
  if (val > 0) b.push(...enc(row("Servicio Valet", money(val))), ...LF);
  if (pen > 0) b.push(...enc(row("Penalizacion", money(pen))), ...LF);
  b.push(...enc(row("Subtotal", money(subtotal))), ...LF);
  if (prop > 0) b.push(...enc(row("Propina", money(prop))), ...LF);
  if (com > 0) b.push(...enc(row("Cargo por servicio", money(com))), ...LF);
  b.push(...BOLD_OFF);
  div();

  // Total
  b.push(...ALIGN_CENTER, ...BOLD_ON, ...enc("TOTAL PAGADO"), ...LF, ...DBL, ...enc(money(total)), ...NORMAL, ...BOLD_OFF, ...LF);
  div();

  // Pie
  b.push(...LF, ...BOLD_ON, ...enc("Gracias por su preferencia"), ...BOLD_OFF, ...LF);
  b.push(...enc("Conserve este comprobante"), ...LF);
  b.push(...ALIGN_LEFT, ...LF, ...LF, ...LF);
  b.push(...FEED_5MM); // margen libre inferior ~5mm (tras el último contenido, antes del corte físico)
  if (cfg.autocorte !== false) b.push(...CUT);
  return new Uint8Array(b);
};

// Sprint 15.0 — Convierte una imagen (dataURL) a comando ESC/POS raster (GS v 0), 1-bit B/N, ancho máx configurable.
export const imageToRasterCommand = (dataUrl, maxWidth = 200) => new Promise((resolve) => {
  try {
    if (!dataUrl || typeof document === "undefined") return resolve([]);
    const img = new Image();
    img.onload = () => {
      try {
        let w = img.width, h = img.height;
        if (!w || !h) return resolve([]);
        if (w > maxWidth) { h = Math.max(1, Math.round(h * maxWidth / w)); w = maxWidth; }
        const wBytes = Math.ceil(w / 8);
        const cw = wBytes * 8;
        const canvas = document.createElement("canvas");
        canvas.width = cw; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, cw, h);
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, cw, h).data;
        const raster = [];
        for (let y = 0; y < h; y++) {
          for (let xb = 0; xb < wBytes; xb++) {
            let byte = 0;
            for (let bit = 0; bit < 8; bit++) {
              const x = xb * 8 + bit;
              const idx = (y * cw + x) * 4;
              const a = data[idx + 3];
              const lum = a < 128 ? 255 : (0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2]);
              if (lum < 128) byte |= (0x80 >> bit); // oscuro => imprime
            }
            raster.push(byte);
          }
        }
        // GS v 0 m xL xH yL yH data  (m=0 normal)
        const cmd = [GS, 0x76, 0x30, 0x00, wBytes & 0xff, (wBytes >> 8) & 0xff, h & 0xff, (h >> 8) & 0xff, ...raster];
        resolve(cmd);
      } catch { resolve([]); }
    };
    img.onerror = () => resolve([]);
    img.src = dataUrl;
  } catch { resolve([]); }
});

// Sprint 14.7 — Boleto DOBLE de Valet. Parte 1 (Cliente) ANÓNIMA con dirección corta + leyenda de adhesión;
// Parte 2 (Talón Operativo) con tipografía ampliada (+1pt) y negritas forzadas.
export const buildValetReceptionTicket = ({
  brand = "PARKNOVA", folio, fecha, placa, marca, color, objetos, qrData,
  nombre, valetOperador, direccionCorta, horario, logo, leyenda, tarifaTexto, cfg = {},
} = {}) => {
  const b = [];
  const { cols, half, dash, clip, clipHalf } = ticketLayout(cfg);
  const HUGE = [GS, 0x21, 0x22]; // GS ! 34 -> 3x (folio enorme)
  const PLUS1 = [GS, 0x21, 0x01]; // GS ! 0x01 -> altura x2 (ancho normal) para el talón operativo
  const seq = folio ? String(folio).split("-").pop() : "";
  const mc = [marca, color].filter(Boolean).join(" / ");
  const header = brand; // Sprint 20.4 — el nombre de la sucursal es el protagonista del encabezado
  const horarioHoy = horarioDeHoy(horario); // Sprint 20.4 — solo el horario del día actual

  // ---------- Parte 1: Boleto del Cliente (anónimo / seguridad alta) ----------
  b.push(...INIT, ...ALIGN_CENTER);
  // Logo opcional protegido: si falla, no detiene la impresión. Centrado + salto de línea de separación.
  try { if (logo && logo.length) { b.push(...logo); b.push(...LF); } } catch { /* logo omitido */ }
  b.push(...BOLD_ON, ...DBL);
  wrapText(String(header), half).forEach((ln) => b.push(...enc(ln), ...LF)); // doble ancho: recorta a cols/2
  b.push(...NORMAL, ...BOLD_OFF);
  if (direccionCorta && String(direccionCorta).trim()) {
    wrapText(String(direccionCorta).trim(), cols).forEach((ln) => b.push(...enc(ln), ...LF));
  }
  // Punto 2 — Horario de atención SOLO del día de hoy (Font A legible, centrada).
  if (horarioHoy && String(horarioHoy).trim()) {
    wrapText(`Horario hoy: ${String(horarioHoy).trim()}`, cols).forEach((ln) => b.push(...enc(ln), ...LF));
  }
  b.push(...BOLD_ON, ...enc("BOLETO DE VALET"), ...BOLD_OFF, ...LF);
  b.push(...enc(dash), ...LF);
  b.push(...BOLD_ON, ...HUGE, ...enc(String(seq || folio || "").slice(0, Math.max(1, Math.floor(cols / 3)))), ...LF, ...NORMAL, ...BOLD_OFF);
  if (folio) b.push(...enc(clip(folio)), ...LF);
  b.push(...LF);
  if (fecha) b.push(...enc(clip(`Fecha/Hora: ${fecha}`)), ...LF);
  // Sprint 20.4 — Tarifa de valet (tarifa fija), igual que el boleto de estacionamiento.
  if (tarifaTexto && String(tarifaTexto).trim()) {
    b.push(...BOLD_ON, ...enc(clip(`Tarifa: ${String(tarifaTexto).trim()}`)), ...BOLD_OFF, ...LF);
  }
  // QR del folio (centrado). SIN placas / marca / color / objetos (regla de seguridad).
  b.push(...LF, ...qrCommands(String(qrData || folio || "")), ...LF);
  wrapText("Escanea para solicitar tu auto o consultar el contrato de adhesion de nuestros servicios.", cols)
    .forEach((ln) => b.push(...enc(ln), ...LF));
  // Sprint 20.4 — Cláusulas: ÚNICAMENTE la leyenda corta configurada por la sucursal
  // (se eliminó el bloque de cláusulas por defecto/hardcoded para evitar duplicidad legal).
  if (leyenda && String(leyenda).trim()) {
    b.push(...LF, ...ALIGN_CENTER);
    wrapText(String(leyenda).trim(), cols).forEach((ln) => b.push(...enc(ln), ...LF));
  }
  b.push(...LF, ...ALIGN_LEFT);
  b.push(...FEED_5MM); // margen libre inferior ~5mm del boleto del cliente (antes del corte intermedio)
  b.push(...CUT); // corte intermedio (boleto del cliente)

  // ---------- Parte 2: Talón Operativo / Control Valet (tipografía ampliada + negritas) ----------
  b.push(...INIT, ...FEED_5MM, ...ALIGN_CENTER, ...BOLD_ON); // margen libre superior ~5mm (criterio corte/gastos/cortesias)
  b.push(...enc("--- TALON OPERATIVO ---"), ...LF, ...LF);
  b.push(...DBL); // folio corto + placas en tamaño grande
  b.push(...enc(clipHalf(`${seq}${placa ? " " + placa : ""}`)), ...LF);
  b.push(...NORMAL);
  if (folio) b.push(...enc(clip(folio)), ...LF);
  b.push(...LF, ...ALIGN_LEFT);
  // Campos destacados: negritas ON + tamaño +1pt (altura x2, ancho normal -> límite = cols, con wrap)
  b.push(...BOLD_ON, ...PLUS1);
  const talon = [
    `CLIENTE: ${nombre || "Sin datos"}`,
    `VEHICULO: ${mc || "Sin datos"}`,
    `PLACAS: ${placa || "Sin datos"}`,
  ];
  talon.forEach((t) => wrapText(t, cols).forEach((ln) => b.push(...enc(ln), ...LF)));
  // OBJETOS DE VALOR: imprime el valor declarado o "SIN OBJETOS DE VALOR" (confirmación explícita).
  // Si NO hay dato → línea en blanco para llenado manual (NUNCA "Ninguno": la ausencia de información
  // NO confirma que no existan objetos de valor).
  const objetosVal = objetos != null ? String(objetos).trim() : "";
  if (objetosVal) {
    wrapText(`OBJETOS: ${objetosVal}`, cols).forEach((ln) => b.push(...enc(ln), ...LF));
  } else {
    b.push(...enc("OBJETOS:"), ...LF, ...enc("_".repeat(cols)), ...LF);
  }
  wrapText(`VALET/ATENDIO: ${valetOperador || "—"}`, cols).forEach((ln) => b.push(...enc(ln), ...LF));
  b.push(...NORMAL, ...BOLD_OFF, ...LF); // restablece fuente para campos manuales
  b.push(...enc("Ubicacion: ____________________"), ...LF, ...LF);
  b.push(...enc("Nivel/Cajon: __________________"), ...LF);
  b.push(...LF, ...FEED_5MM); // margen libre inferior ~5mm (criterio corte/gastos/cortesias)
  b.push(...CUT); // corte final (talón operativo)

  return new Uint8Array(b);
};

// Sprint 20.3 — Devuelve SOLO el horario correspondiente al día de hoy a partir de una
// cadena tipo "L-V 10:00-20:00 | SÁB 10:00-20:00 | DOM 10:00-16:00". Si no logra
// identificar el segmento del día, regresa la cadena completa (fallback seguro).
const _DIA_HOY = () => getLocalWeekday(); // 0=Dom ... 6=Sáb (en la zona horaria de la sucursal)
const _TOKEN_DIA = { DOM: 0, DOMINGO: 0, D: 0, LUN: 1, LUNES: 1, L: 1, MAR: 2, MARTES: 2, MIE: 3, MIERCOLES: 3, X: 3, JUE: 4, JUEVES: 4, J: 4, VIE: 5, VIERNES: 5, V: 5, SAB: 6, SABADO: 6, S: 6 };
const _norm = (s) => String(s).toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
const _tokenIdx = (tok) => {
  const t = _norm(tok);
  if (t in _TOKEN_DIA) return _TOKEN_DIA[t];
  if (t.length > 1 && t.slice(0, 3) in _TOKEN_DIA) return _TOKEN_DIA[t.slice(0, 3)];
  return null;
};
const _diasDeSegmento = (dayPart) => {
  const raw = _norm(dayPart);
  const dias = new Set();
  raw.split(/[,;]|\bY\b/).forEach((chunk) => {
    const rango = chunk.match(/^\s*([A-Z]+)\s*(?:-|\bA\b)\s*([A-Z]+)\s*$/);
    if (rango) {
      const a = _tokenIdx(rango[1]);
      const b = _tokenIdx(rango[2]);
      if (a != null && b != null) {
        let i = a;
        for (let n = 0; n < 7; n++) { dias.add(i); if (i === b) break; i = (i + 1) % 7; }
      }
    } else {
      const idx = _tokenIdx(chunk.trim());
      if (idx != null) dias.add(idx);
    }
  });
  return dias;
};
export const horarioDeHoy = (horario) => {
  if (!horario || !String(horario).trim()) return null;
  const hoy = _DIA_HOY();
  const segmentos = String(horario).split(/\||\n/).map((s) => s.trim()).filter(Boolean);
  for (const seg of segmentos) {
    const idxTiempo = seg.search(/\d/);
    const dayPart = idxTiempo > 0 ? seg.slice(0, idxTiempo) : seg;
    const dias = _diasDeSegmento(dayPart);
    if (dias.has(hoy)) return seg.trim();
  }
  return String(horario).trim(); // fallback: cadena completa
};

// Sprint 20.3 — Boleto de ESTACIONAMIENTO (autoservicio). SIN Marca/Color/Placas.
// Incluye tarifa por hora/fracción, horario SOLO de hoy, leyenda corta y QR que
// redirige a la landing de autoservicio (/e/:folio).
export const buildEstacionamientoTicket = ({
  brand = "PARKNOVA", folio, fecha, tarifaTexto, horarioHoy, leyenda,
  qrUrl, direccionCorta, logo, cfg = {},
} = {}) => {
  const b = [];
  const { cols, half, dash, clip } = ticketLayout(cfg);
  const third = Math.max(1, Math.floor(cols / 3)); // límite para folio en HUGE (3x)
  const HUGE = [GS, 0x21, 0x22];
  const seq = folio ? String(folio).split("-").pop() : "";

  b.push(...INIT, ...ALIGN_CENTER);
  try { if (logo && logo.length) { b.push(...logo); b.push(...LF); } } catch { /* logo omitido */ }
  b.push(...BOLD_ON, ...DBL);
  wrapText(String(brand), half).forEach((ln) => b.push(...enc(ln), ...LF));
  b.push(...NORMAL, ...BOLD_OFF);
  if (direccionCorta && String(direccionCorta).trim()) {
    wrapText(String(direccionCorta).trim(), cols).forEach((ln) => b.push(...enc(ln), ...LF));
  }
  if (horarioHoy && String(horarioHoy).trim()) {
    wrapText(`Horario hoy: ${String(horarioHoy).trim()}`, cols).forEach((ln) => b.push(...enc(ln), ...LF));
  }
  b.push(...BOLD_ON, ...enc(clip("BOLETO DE ESTACIONAMIENTO")), ...BOLD_OFF, ...LF);
  b.push(...enc(dash), ...LF);
  b.push(...BOLD_ON, ...HUGE, ...enc(String(seq || folio || "").slice(0, third)), ...LF, ...NORMAL, ...BOLD_OFF);
  if (folio) b.push(...enc(clip(folio)), ...LF);
  b.push(...LF);
  if (fecha) b.push(...enc(clip(`Entrada: ${fecha}`)), ...LF);
  if (tarifaTexto && String(tarifaTexto).trim()) {
    b.push(...BOLD_ON, ...enc(clip(`Tarifa: ${String(tarifaTexto).trim()}`)), ...BOLD_OFF, ...LF);
  }
  // QR que abre la landing de autoservicio (/e/:folio) al escanear con el celular.
  b.push(...LF, ...qrCommands(String(qrUrl || folio || "")), ...LF);
  wrapText("Escanea para ver tarifa, ubicacion y contrato. Presenta este boleto en caseta para tu salida.", cols)
    .forEach((ln) => b.push(...enc(ln), ...LF));
  if (leyenda && String(leyenda).trim()) {
    b.push(...LF);
    wrapText(String(leyenda).trim(), cols).forEach((ln) => b.push(...enc(ln), ...LF));
  }
  b.push(...LF, ...enc("Conserve este boleto."), ...LF);
  b.push(...LF, ...LF, ...ALIGN_LEFT);
  if (cfg.abrirCajon) b.push(...DRAWER);
  b.push(...FEED_5MM); // margen libre inferior ~5mm (tras el último contenido, antes del corte físico)
  if (cfg.autocorte) b.push(...CUT);
  return new Uint8Array(b);
};

// Envía por hardware (bluetooth/local/usb) o devuelve "nativo" para que el caller use window.print()/popup.
// USB con error (desconectado / sin permiso) => fallback silencioso a nativo (el usuario nunca se queda sin ticket).
export const printOrFallback = async (bytes, cfg) => {
  const c = cfg || loadPrinterConfig();
  if (c.modo === "nativo") return "nativo";
  try {
    await printBuffer(bytes, c);
    return "handled";
  } catch (e) {
    if (c.modo === "usb") return "nativo";
    throw e;
  }
};

// Abre el cajón en cada pago exitoso (si la preferencia está activa). Nativo no puede abrir cajón vía navegador.
export const openDrawer = async (cfg) => {
  const c = cfg || loadPrinterConfig();
  if (c.modo === "nativo") return "nativo";
  try {
    await printBuffer(buildDrawerPulse(), c);
    return "handled";
  } catch (e) {
    if (c.modo === "usb") return "nativo";
    throw e;
  }
};
